import React, { useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, FormControl, FormLabel, HStack, Input, Select, Textarea, useToast,
} from "@chakra-ui/react";
import { editTask } from "../../services/messagingService";
import { updateOrderItem } from "../../services/clinicComplianceService";
import { emitWorkItemsChanged } from "../../utils/workItemsBus";
import { PRIORITIES, PRIORITY_META, normalizePriority } from "../../utils/taskPriority";

const day = (value) => (value || "").slice(0, 10);

// Edit on a task you assigned: its title, description, due date and priority
// (who has it changes with Reassign). task: a Tasks-tab item { kind, id, title,
// description, due, priority, schema }. onSaved(task) gets the task as the
// server saved it. An order has only its text (the title) and priority.
export default function EditTaskModal({ task, currentUserId, onClose, onSaved }) {
  const isOrder = task.kind === "order";
  const [title, setTitle] = useState(task.title || "");
  const [description, setDescription] = useState(task.description || "");
  const [due, setDue] = useState(day(task.due));
  const [priority, setPriority] = useState(normalizePriority(task.priority));
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const changed =
    title.trim() !== (task.title || "").trim() ||
    description.trim() !== (task.description || "").trim() ||
    due !== day(task.due) ||
    priority !== normalizePriority(task.priority);

  const save = async () => {
    setBusy(true);
    try {
      if (isOrder) {
        const res = await updateOrderItem(task.id, {
          description: title.trim(), priority, actor_user_id: currentUserId,
        }, { schema: task.schema });
        onSaved?.({ title: title.trim(), description: null, due_date: null, priority: res?.priority || priority });
      } else {
        const res = await editTask(task.id, currentUserId, {
          title: title.trim(),
          description: description.trim() || null,
          dueDate: due || null,
          priority,
        });
        onSaved?.(res?.task);
      }
      emitWorkItemsChanged({ kind: task.kind || "task", id: task.id, action: "edit" });
      toast({ title: isOrder ? "Order updated" : "Task updated", status: "success", duration: 2000 });
      onClose();
    } catch (err) {
      toast({ title: `Couldn't save the ${isOrder ? "order" : "task"}`, description: err?.message, status: "error", duration: 5000 });
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" blockScrollOnMount={false} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isOrder ? "Edit order" : "Edit task"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel>{isOrder ? "Order" : "Title"}</FormLabel>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormControl>
          {!isOrder && (
            <>
              <FormControl mb={3}>
                <FormLabel>Description (optional)</FormLabel>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={6} />
              </FormControl>
              <FormControl>
                <FormLabel>Due date (optional)</FormLabel>
                <HStack>
                  <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
                  {due && (
                    <Button size="sm" variant="ghost" aria-label="Clear due date" onClick={() => setDue("")}>
                      Clear
                    </Button>
                  )}
                </HStack>
              </FormControl>
            </>
          )}
          <FormControl mt={isOrder ? 0 : 3}>
            <FormLabel>Priority</FormLabel>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{PRIORITY_META[p].label}</option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Back</Button>
          <Button colorScheme="blue" onClick={save} isLoading={busy} isDisabled={!title.trim() || !changed}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
